import { isComparisonReport } from './comparisonColumnModel';
import type { Report, ReportRow } from './generatedReportsTypes';

/**
 * Slice a multi-column TIME-bucketed report to a single DATE_RANGE column and
 * mark that column as `isAggSum` so `buildSankeyData` reads the period-specific
 * values instead of the across-range aggregate. Returns null when no column
 * matches the given date range.
 */
export function filterReportToSinglePeriod(
  report: Report | null | undefined,
  startOn: string,
  endOn: string,
): Report | null {
  if (!report?.columns?.length) return null;
  const col = report.columns.find(
    (c): c is typeof c & { id: string; headerType: 'DATE_RANGE' } =>
      c.headerType === 'DATE_RANGE' &&
      c.headerValue.startOn === startOn &&
      c.headerValue.endOn === endOn &&
      c.id != null,
  );
  if (!col) return null;
  return {
    ...report,
    columns: [{ ...col, isAggSum: true }],
    rows: report.rows.map((row) => ({
      ...row,
      cellData: row.cellData?.filter((cell) => cell.columnId === col.id) ?? [],
    })),
  };
}

export type SankeyDatum = {
  from: string;
  to: string;
  value: number;
  /**
   * Overrides the `to` node's displayed label/tooltip value (amount + percent) without
   * affecting sizing — e.g. Total Spending netted for a refund credit flowing into it.
   */
  overrideValue?: number;
};

/**
 * Breadcrumb-style ancestry entry for a Sankey node — mirrors the shape of
 * `NameData` used by the drill-down machinery on the web side, so wrappers
 * can pass it directly into `handlePieClick` as `originalRow.namesMap`.
 */
export type SankeyNameEntry = {
  entities: string[];
  entityId: string;
  rowId: string;
  name: string;
  displayName: string;
  total?: number;
};

/**
 * Everything a drill-down handler needs when a Sankey node is clicked:
 * the underlying report row (for entityId / id) plus the full ancestry
 * names so the register shows the correct breadcrumb hierarchy.
 */
export type SankeyNodeContext = {
  row: ReportRow;
  namesMap: SankeyNameEntry[];
};

export type SankeyNodeLookup = Record<string, SankeyNodeContext>;

export type SankeyData = {
  flows: SankeyDatum[];
  rowsByNodeName: SankeyNodeLookup;
  /** The report column id whose cell holds the across-range aggregate ("Total"). */
  aggColumnId: string | undefined;
};

export type SankeyDataOptions = {
  showSubcategory?: boolean;
  /** OpenSaveReports layout: one income hub instead of a Total Spending bridge. */
  layout?: 'classic' | 'directHub';
  /**
   * When false, collapse the income side to a single tier: every income leaf
   * (and net-positive expense credit) flows directly into Total Income, with
   * no intermediate group nodes (e.g. "Personal Income").
   */
  showIncomeBreakdown?: boolean;
};

const EMPTY: SankeyData = { flows: [], rowsByNodeName: {}, aggColumnId: undefined };

export const TOTAL_INCOME_NODE = 'Total Income';
export const TOTAL_SPENDING_NODE = 'Total Spent';
export const NET_INCOME_NODE = 'Remaining';

const INCOME_ROOT_ENTITY_ID = 'INCOME';
const EXPENSE_ROOT_ENTITY_ID = 'EXPENSE';
const INCOME_SECTION_ENTITY_IDS = new Set(['INCOME_BUSINESS', 'INCOME_PERSONAL']);
// Business/Personal wrapper between EXPENSE and real categories; QCS sends either spelling (see comparisonChartModel.ts).
const EXPENSE_SECTION_ENTITY_IDS = new Set(['EXPENSE_BUSINESS', 'EXPENSE_PERSONAL', 'BUSINESS', 'PERSONAL']);

const parseCellValue = (raw: string | undefined): number => {
  if (!raw) return 0;
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Return a row's total across the whole date range.
 *
 * Prefer the pre-aggregated "Total" column (flagged `isAggSum`) when the report
 * includes one — that's the server's canonical rollup. When it doesn't (e.g.
 * the user grouped columns by Account/Category instead of Time), sum every
 * cell. This mirrors the pattern at generatedReportsSelectors.ts:447.
 */
const getRowTotal = (row: ReportRow, aggColumnId: string | undefined): number => {
  const cells = row.cellData ?? [];
  if (aggColumnId) {
    const aggCell = cells.find((c) => c.columnId === aggColumnId);
    if (aggCell?.value !== undefined) {
      return parseCellValue(aggCell.value);
    }
  }
  return cells.reduce((sum, cell) => sum + parseCellValue(cell.value), 0);
};

const findAggSumColumnId = (report: Report): string | undefined => report.columns.find((col) => col.isAggSum)?.id;

/**
 * Walk a subtree and return only the leaf rows (rows with no children).
 * If the starting row itself has no children, returns [startingRow].
 */
const collectLeaves = (startingId: string, rowsByParent: Map<string, ReportRow[]>): ReportRow[] => {
  const children = rowsByParent.get(startingId) ?? [];
  if (children.length === 0) return [];
  const leaves: ReportRow[] = [];
  for (const child of children) {
    const grandChildren = child.id ? (rowsByParent.get(child.id) ?? []) : [];
    if (grandChildren.length === 0) {
      leaves.push(child);
    } else {
      leaves.push(...collectLeaves(child.id ?? '', rowsByParent));
    }
  }
  return leaves;
};

// Descends through a Business/Personal wrapper to the real categories beneath it; kept if childless.
const resolveExpenseCategoryTier = (topLevel: ReportRow[], rowsByParent: Map<string, ReportRow[]>): ReportRow[] =>
  topLevel.flatMap((row) => {
    if (!EXPENSE_SECTION_ENTITY_IDS.has(row.entityId ?? '')) return [row];
    const children = row.id ? (rowsByParent.get(row.id) ?? []) : [];
    return children.length > 0 ? children : [row];
  });

/**
 * Build Sankey flow data from a NET_INCOME report.
 *
 * Layout (Monarch-style):
 *   income leaves → Total Income → Total Spending → top-level expense category → expense leaves
 *   Total Income → Net Income (any surplus when income > expenses)
 *
 * - Income side is flattened to leaves so we don't show a redundant "Personal
 *   Income" middle node when there's only one top-level income category.
 * - Expense side keeps the two-level hierarchy (parent group → child leaves)
 *   because expense trees have meaningful grouping (Auto & Transport → Gas,
 *   Parking). Top-level expenses without children draw a single flow.
 * - The Business/Personal wrapper tier is transparent when `showSubcategory` is false (see `resolveExpenseCategoryTier`).
 * - Rows whose signed total has the wrong sign for their type (e.g. a
 *   refund-dominated expense category with a net positive total) are skipped
 *   so we don't draw backwards flows.
 * - Net-positive ("credit") expense categories/leaves feed directly into
 *   Total Spending (top-level categories) or their parent category (leaves
 *   within a still-negative category) instead of Total Income — a refund
 *   offsets spending, it isn't new income.
 * - Values are taken from the aggregate-sum ("Total") column when present,
 *   otherwise by summing every cell. Flows with zero or wrong-sign values are
 *   skipped. If the report is missing its INCOME or EXPENSE root row,
 *   returns [].
 */
export const buildSankeyData = (report: Report | null | undefined, options?: SankeyDataOptions): SankeyData => {
  const showSubcategory = options?.showSubcategory ?? true;
  const layout = options?.layout ?? 'classic';
  const showIncomeBreakdown = options?.showIncomeBreakdown ?? true;
  // Comparison reports are grid-only in v1. `findAggSumColumnId` takes the FIRST isAggSum column,
  // but a comparison report has one per group, and the no-agg fallback sums every cell in a row —
  // which would add VALUE, CUMULATIVE_VALUE and DELTA columns together and inflate every flow.
  if (!report?.rows?.length || !report.columns?.length || isComparisonReport(report)) {
    return EMPTY;
  }

  const aggColumnId = findAggSumColumnId(report);

  const rowsByParent = new Map<string, ReportRow[]>();
  for (const row of report.rows) {
    const parentId = row.parentId ?? '-1';
    const bucket = rowsByParent.get(parentId);
    if (bucket) {
      bucket.push(row);
    } else {
      rowsByParent.set(parentId, [row]);
    }
  }

  const rootRows = rowsByParent.get('-1') ?? [];
  const incomeRoot = rootRows.find((r) => r.entityId === INCOME_ROOT_ENTITY_ID);
  const expenseRoot = rootRows.find((r) => r.entityId === EXPENSE_ROOT_ENTITY_ID);
  if (!incomeRoot && !expenseRoot) {
    // The reports backend omits a root section entirely (rather than returning
    // it with a $0 total) whenever a report/period has no income or no expense
    // transactions at all — only bail when BOTH sides are missing (nothing to draw).
    return EMPTY;
  }
  // Total Income's net available balance — every inflow (income leaves/groups)
  // minus every outflow already drawn from the hub (loss edges for net-negative
  // leaves/groups). This is the ceiling for how much can flow onward into
  // Total Spending; see the deficit cap below.
  const totalIncome = incomeRoot ? getRowTotal(incomeRoot, aggColumnId) : 0;

  const flows: SankeyDatum[] = [];
  const rowsByNodeName: SankeyNodeLookup = {};

  // Walk a row's parent chain back toward the INCOME/EXPENSE root and return
  // the ancestry (root-to-leaf), filtered to category rows. The root INCOME
  // and EXPENSE wrappers are dropped.
  const rowsById = new Map<string, ReportRow>();
  for (const r of report.rows) {
    if (r.id) rowsById.set(r.id, r);
  }
  const buildNamesMap = (row: ReportRow): SankeyNameEntry[] => {
    const chain: ReportRow[] = [];
    let cursor: ReportRow | undefined = row;
    while (cursor) {
      const parentId = cursor.parentId;
      // Stop before including the top-level INCOME/EXPENSE wrapper row —
      // handlePieClick's filter only matches the uppercase entity strings
      // ("INCOME"/"EXPENSE"), not the display names ("Income"/"Expenses"),
      // so if we pushed them here they'd end up in the breadcrumb.
      if (!parentId || parentId === '-1') break;
      chain.unshift(cursor);
      cursor = rowsById.get(parentId);
    }
    return chain.map((r) => {
      const total = getRowTotal(r, aggColumnId);
      const entityId = r.entityId ?? '';
      const name = r.name ?? '';
      return {
        entities: [entityId],
        entityId,
        rowId: r.id ?? entityId,
        name,
        displayName: name,
        total,
      };
    });
  };

  const registerNode = (nodeName: string, row: ReportRow): void => {
    rowsByNodeName[nodeName] = { row, namesMap: buildNamesMap(row) };
  };

  // Sankey nodes are keyed by display name, so two rows with the same `name`
  // (e.g. Business Uncategorized vs Personal Uncategorized) would otherwise
  // merge into a single node. When names collide across emittable rows,
  // qualify the display with enough context to split them apart:
  //   1. `secondaryText` when Simplifi provided it (matches the grid),
  //   2. otherwise the immediate parent's name, as long as the parent isn't
  //      the top-level INCOME/EXPENSE wrapper (prefixing with "Expenses"
  //      reads awkwardly and isn't disambiguating).
  const collidedNames = new Set<string>();
  const nameCounts = new Map<string, number>();
  for (const r of report.rows) {
    if (!r.name || !r.parentId || r.parentId === '-1') continue;
    const next = (nameCounts.get(r.name) ?? 0) + 1;
    nameCounts.set(r.name, next);
    if (next > 1) collidedNames.add(r.name);
  }
  const hasIncomeSuffix = (name: string): boolean => /\bincome$/i.test(name.trim());
  const hasExpenseSuffix = (name: string): boolean => /\bexpenses?$/i.test(name.trim());
  const displayNameFor = (row: ReportRow): string => {
    const name = row.name ?? '';
    // The income and expense Transfer rows can both sit under a Personal/Business wrapper.
    // Parent-name qualification alone merges opposite directions into one
    // Sankey node, reversing the layout or creating a cycle when a bridge exists.
    if (row.entityId === 'TRANSFER_INCOME' || row.entityId?.startsWith('TRANSFER_INCOME (')) {
      const parentEntityId = rowsById.get(row.parentId ?? '')?.entityId;
      if (parentEntityId === 'INCOME_PERSONAL') {
        return 'Personal Transfer (credit)';
      } else if (parentEntityId === 'INCOME_BUSINESS') {
        return 'Business Transfer (credit)';
      }
    }
    if (!collidedNames.has(name)) return name;
    if (row.secondaryText) return `${row.secondaryText} ${name}`.trim();
    if (row.parentId) {
      const parent = rowsById.get(row.parentId);
      if (parent?.name && parent.parentId && parent.parentId !== '-1') {
        return `${parent.name} ${name}`.trim();
      }
      // Parent is the INCOME / EXPENSE root wrapper — use that context to
      // distinguish top-level groups that collide across sides (e.g. an
      // income "Business" vs an expense "Business", which otherwise merge
      // into a single amcharts node).
      if (parent?.entityId === INCOME_ROOT_ENTITY_ID && !hasIncomeSuffix(name)) {
        return `${name} Income`;
      }
      if (parent?.entityId === EXPENSE_ROOT_ENTITY_ID && !hasExpenseSuffix(name)) {
        return `${name} Expense`;
      }
    }
    return name;
  };

  // Income side — when showIncomeBreakdown is true and Simplifi splits income
  // into multiple top-level groups (e.g. Business + Personal), mirror the
  // expense layout: leaf → group → Total Income. When there's only a single
  // top-level group, or showIncomeBreakdown is false, flatten so every leaf
  // feeds Total Income directly with no intermediate group node.
  const incomeTopLevel = incomeRoot
    ? (rowsByParent.get(incomeRoot.id ?? '') ?? [])
        .slice()
        .sort((a, b) => getRowTotal(b, aggColumnId) - getRowTotal(a, aggColumnId))
    : [];

  const emitIncomeLeaf = (leaf: ReportRow, into: string): void => {
    const parent = rowsById.get(leaf.parentId ?? '');
    // The flattened dashboard omits the parent income category. QCS names its
    // direct-booking child "<category> (Other)"; with no parent node visible,
    // show the category's usual name instead.
    const leafDisplay =
      !showIncomeBreakdown && parent?.name && leaf.name === `${parent.name} (Other)`
        ? parent.name
        : displayNameFor(leaf);
    const value = getRowTotal(leaf, aggColumnId);
    if (!leafDisplay || value === 0) return;

    if (value < 0) {
      // Net-negative leaf (e.g. a refund on an income source) — show as an
      // outflow from the group node (or Total Income when ungrouped).
      const lossName = `${leafDisplay} (loss)`;
      flows.push({ from: into, to: lossName, value: Math.abs(value) });
      registerNode(lossName, leaf);
      return;
    }

    // If a leaf's name matches its immediate sink (the group), tag it so the
    // Sankey doesn't draw a self-loop — same precedent as the expense side.
    const safeName = leafDisplay === into ? `${leafDisplay} (Other)` : leafDisplay;
    flows.push({ from: safeName, to: into, value });
    registerNode(safeName, leaf);
  };

  if (incomeRoot) {
    if (
      showIncomeBreakdown &&
      (incomeTopLevel.length > 1 ||
        (layout === 'directHub' && incomeTopLevel.some((group) => INCOME_SECTION_ENTITY_IDS.has(group.entityId ?? ''))))
    ) {
      for (const group of incomeTopLevel) {
        const groupName = displayNameFor(group);
        const groupTotal = getRowTotal(group, aggColumnId);
        if (!groupName || groupTotal === 0) continue;

        if (groupTotal < 0) {
          // Entire group is underwater — render as a loss outflow from Total
          // Income, mirroring the single-leaf loss handling.
          const lossName = `${groupName} (loss)`;
          flows.push({ from: TOTAL_INCOME_NODE, to: lossName, value: Math.abs(groupTotal) });
          registerNode(lossName, group);
          continue;
        }

        const children = group.id ? (rowsByParent.get(group.id) ?? []) : [];
        if (children.length === 0) {
          // Top-level row with no children behaves like a plain income leaf.
          flows.push({ from: groupName, to: TOTAL_INCOME_NODE, value: groupTotal });
          registerNode(groupName, group);
          continue;
        }

        flows.push({ from: groupName, to: TOTAL_INCOME_NODE, value: groupTotal });
        registerNode(groupName, group);

        const leaves = collectLeaves(group.id ?? '', rowsByParent)
          .slice()
          .sort((a, b) => getRowTotal(b, aggColumnId) - getRowTotal(a, aggColumnId));
        for (const leaf of leaves) emitIncomeLeaf(leaf, groupName);
      }
    } else {
      const incomeLeaves = collectLeaves(incomeRoot.id ?? '', rowsByParent)
        .slice()
        .sort((a, b) => getRowTotal(b, aggColumnId) - getRowTotal(a, aggColumnId));
      for (const leaf of incomeLeaves) emitIncomeLeaf(leaf, TOTAL_INCOME_NODE);
    }
  }

  // Expense side — three-level hierarchy: Total Income → Total Spending →
  // top-level expense group → leaf expense category. If a top-level expense
  // has no children, the parent-to-leaf flow is omitted. Sort descending by
  // magnitude at every level so the biggest spend stacks on top and the tail
  // fans out below. Collect expense flows in a buffer so the Total Income →
  // Total Spending edge (whose value we only know after the loop) can be
  // emitted before the per-category flows.
  const expenseRootChildren = expenseRoot ? (rowsByParent.get(expenseRoot.id ?? '') ?? []) : [];
  // Only the single-tier classic shape collapses the wrapper; directHub and two-tier classic keep it.
  const collapseUsageWrappers = layout !== 'directHub' && !showSubcategory;
  const expenseTopLevel = (
    collapseUsageWrappers ? resolveExpenseCategoryTier(expenseRootChildren, rowsByParent) : expenseRootChildren
  )
    .slice()
    .sort((a, b) => Math.abs(getRowTotal(b, aggColumnId)) - Math.abs(getRowTotal(a, aggColumnId)));
  const expenseFlows: SankeyDatum[] = [];
  // Top-level expense credits (net-positive categories) feed directly into Total
  // Spending, so a refund reduces net spending instead of inflating income.
  // Retain their flows to set the report's net-total label below.
  const creditFlowsIntoSpending: SankeyDatum[] = [];
  for (const cat of expenseTopLevel) {
    const catName = displayNameFor(cat);
    const catRaw = getRowTotal(cat, aggColumnId);
    if (!catName || catRaw === 0) continue;

    if (catRaw > 0) {
      // Net-positive expense (refunds exceeded charges) — show as an inflow
      // into Total Spending: real money that offsets spending, not income.
      const creditName = `${catName} (credit)`;
      const creditFlow: SankeyDatum = { from: creditName, to: TOTAL_SPENDING_NODE, value: catRaw };
      flows.push(creditFlow);
      creditFlowsIntoSpending.push(creditFlow);
      registerNode(creditName, cat);
      continue;
    }

    const catTotal = Math.abs(catRaw);
    expenseFlows.push({ from: TOTAL_SPENDING_NODE, to: catName, value: catTotal });
    registerNode(catName, cat);

    // The direct-hub layout needs the full hierarchy before it can decide
    // whether to retain the final subcategory column.
    if (showSubcategory || layout === 'directHub') {
      const leaves = (cat.id ? (rowsByParent.get(cat.id) ?? []) : [])
        .slice()
        .sort((a, b) => Math.abs(getRowTotal(b, aggColumnId)) - Math.abs(getRowTotal(a, aggColumnId)));
      for (const leaf of leaves) {
        const leafRaw = getRowTotal(leaf, aggColumnId);
        const leafDisplay = displayNameFor(leaf);
        if (!leafDisplay || leafRaw === 0) continue;

        if (leafRaw > 0) {
          // Net-positive leaf expense — show as credit flowing into the parent.
          const creditLeafName = `${leafDisplay} (credit)`;
          expenseFlows.push({ from: creditLeafName, to: catName, value: leafRaw });
          registerNode(creditLeafName, leaf);
          continue;
        }

        // Simplifi sometimes returns a leaf whose name matches its parent — it's
        // the "other" rollup of transactions booked directly to the parent
        // category. Append " (Other)" so the Sankey doesn't draw a self-loop.
        const leafName = leafDisplay === catName ? `${leafDisplay} (Other)` : leafDisplay;
        expenseFlows.push({ from: catName, to: leafName, value: Math.abs(leafRaw) });
        registerNode(leafName, leaf);
      }
    }

    // Business/Personal is a report section, not a spending category. In the
    // direct-hub layout, retain its categories and optionally one further
    // subcategory level so the report hierarchy stays visible.
    if (layout === 'directHub' && showSubcategory && EXPENSE_SECTION_ENTITY_IDS.has(cat.entityId ?? '')) {
      const categories = cat.id ? (rowsByParent.get(cat.id) ?? []) : [];
      for (const category of categories) {
        const categoryName = displayNameFor(category);
        if (!categoryName) continue;

        const subcategories = (category.id ? (rowsByParent.get(category.id) ?? []) : [])
          .slice()
          .sort((a, b) => Math.abs(getRowTotal(b, aggColumnId)) - Math.abs(getRowTotal(a, aggColumnId)));
        for (const subcategory of subcategories) {
          const subcategoryRaw = getRowTotal(subcategory, aggColumnId);
          const subcategoryDisplay = displayNameFor(subcategory);
          if (!subcategoryDisplay || subcategoryRaw === 0) continue;

          if (subcategoryRaw > 0) {
            const creditName = `${subcategoryDisplay} (credit)`;
            expenseFlows.push({ from: creditName, to: categoryName, value: subcategoryRaw });
            registerNode(creditName, subcategory);
            continue;
          }

          const subcategoryName =
            subcategoryDisplay === categoryName ? `${subcategoryDisplay} (Other)` : subcategoryDisplay;
          expenseFlows.push({ from: categoryName, to: subcategoryName, value: Math.abs(subcategoryRaw) });
          registerNode(subcategoryName, subcategory);
        }
      }
    }
  }

  // Use the report's net expense total for the hub bridge, just as the KPI does.
  // Visible category and credit rollups do not always reconcile by subtraction
  // under a single QBP usage. Cap the bridge at real income for a deficit.
  const reportExpenseTotal = expenseRoot ? getRowTotal(expenseRoot, aggColumnId) : 0;
  const spendingNeededFromIncome = Math.max(-reportExpenseTotal, 0);
  const incomeIntoSpending = Math.min(totalIncome, spendingNeededFromIncome);
  if (incomeIntoSpending > 0) {
    flows.push({ from: TOTAL_INCOME_NODE, to: TOTAL_SPENDING_NODE, value: incomeIntoSpending });
  }
  flows.push(...expenseFlows);

  // Tag the credit flows with the net spend total (see `overrideValue`).
  if (creditFlowsIntoSpending.length > 0) {
    // The EXPENSE root is the same period total used by the dashboard KPI.
    // Category leaves can contain transfer/credit rollups that do not reconcile
    // by simple subtraction, particularly with only one QBP usage selected.
    const netSpending = Math.round(Math.abs(reportExpenseTotal) * 100) / 100;
    creditFlowsIntoSpending.forEach((flow) => {
      flow.overrideValue = netSpending;
    });
  }

  // A net credit adds to Remaining only after covering any net income loss.
  // The KPI uses this same signed period total for Remaining.
  const signedRemaining = Math.round((totalIncome + reportExpenseTotal) * 100) / 100;
  const creditToRemaining = Math.min(Math.round(reportExpenseTotal * 100) / 100, signedRemaining);
  // When income is a net loss, the expense credit covers that loss first. This
  // also connects the credit and loss sides of the classic chart in that case.
  const creditToIncomeLoss =
    Math.round(Math.min(Math.max(-totalIncome, 0), Math.max(reportExpenseTotal, 0)) * 100) / 100;
  if (layout !== 'directHub' && creditToIncomeLoss > 0) {
    flows.push({
      from: TOTAL_SPENDING_NODE,
      to: TOTAL_INCOME_NODE,
      value: creditToIncomeLoss,
      overrideValue: totalIncome,
    });
  }
  if (layout !== 'directHub' && creditToRemaining > 0) {
    flows.push({ from: TOTAL_SPENDING_NODE, to: NET_INCOME_NODE, value: creditToRemaining });
  }

  // Surplus terminal: whatever the user didn't spend flows into the Remaining node.
  // When expenses exceed income (deficit), don't emit anything — Monarch lets
  // the Sankey go unbalanced rather than showing a synthetic Deficit source.
  // Round to the nearest cent (307.78 − 116.78 = 190.99999999999997 otherwise).
  const surplus =
    layout === 'directHub' ? signedRemaining : Math.round((totalIncome + Math.min(reportExpenseTotal, 0)) * 100) / 100;
  if (surplus > 0) {
    flows.push({ from: TOTAL_INCOME_NODE, to: NET_INCOME_NODE, value: surplus });
  }

  const result = { flows, rowsByNodeName, aggColumnId };
  if (layout !== 'directHub') return result;

  // Keep the income hub and let it feed each expense section directly. This gives
  // OpenSaveReports a Monarch-style single middle bar while preserving the classic
  // Total Spending bridge for the Spending Dashboard. Remap both `from` and `to`
  // occurrences of Total Spending — a credit flow's `to` is Total Spending too, and
  // leaving it unmapped would strand it as a disconnected node.
  const directFlows = flows
    .filter(({ from, to }) => from !== TOTAL_INCOME_NODE || to !== TOTAL_SPENDING_NODE)
    .map((flow) => ({
      ...flow,
      from: flow.from === TOTAL_SPENDING_NODE ? TOTAL_INCOME_NODE : flow.from,
      to: flow.to === TOTAL_SPENDING_NODE ? TOTAL_INCOME_NODE : flow.to,
    }));

  if (showSubcategory) return { ...result, flows: directFlows };

  const directExpenseRoots = directFlows
    .filter(({ from, to }) => from === TOTAL_INCOME_NODE && to !== NET_INCOME_NODE)
    .map(({ to }) => to);
  const expenseNodes = new Set(directExpenseRoots);
  const childrenByParent = new Map<string, string[]>();
  directFlows.forEach(({ from, to }) => {
    const children = childrenByParent.get(from) ?? [];
    children.push(to);
    childrenByParent.set(from, children);
  });
  for (const nodeName of expenseNodes) {
    childrenByParent.get(nodeName)?.forEach((childName) => expenseNodes.add(childName));
  }

  const expenseSectionNames = new Set(
    Object.entries(rowsByNodeName)
      .filter(([, context]) => EXPENSE_SECTION_ENTITY_IDS.has(context.row.entityId ?? ''))
      .map(([nodeName]) => nodeName),
  );

  return {
    ...result,
    flows: directFlows.filter(
      ({ from }) => !expenseNodes.has(from) || from === TOTAL_INCOME_NODE || expenseSectionNames.has(from),
    ),
  };
};
